import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { api } from '@/api/client'
import { Button } from '@components/ui/Button'
import { TokenRevealCard } from './TokenRevealCard'

interface RegisterModalProps {
  onClose: () => void
  redirectTo?: string
}

export function RegisterModal({ onClose, redirectTo }: RegisterModalProps) {
  const [step, setStep] = useState<'form' | 'token'>('form')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [rawToken, setRawToken] = useState('')
  const navigate = useNavigate()
  const { checkSession } = useAuthStore()

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await api.registration.register(email || undefined)
      if (result.success) {
        setRawToken(result.rawToken)
        setStep('token')
      } else {
        setError(result.message || 'Registration failed.')
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { detail?: string } } }).response?.data?.detail ??
            'Registration failed. Try again later.')
          : 'Registration failed. Try again later.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async () => {
    // Backend already set the session cookie during registration
    await checkSession()
    onClose()
    navigate(redirectTo || '/admin')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="card p-6 max-w-md w-full">
        {step === 'form' ? (
          <>
            <h3 className="text-lg font-bold text-gray-100 mb-1">Create Account</h3>
            <p className="text-sm text-gray-400 mb-5">
              Register to get an API token. No password needed — your token is your identity.
            </p>

            <form onSubmit={handleRegister} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="reg-email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email <span className="text-gray-500">(optional, for contact only)</span>
                </label>
                <input
                  id="reg-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full"
                />
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="flex-1" loading={loading}>
                  Register
                </Button>
                <Button variant="secondary" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h3 className="text-lg font-bold text-gray-100 mb-1">Your API Token</h3>
            <p className="text-sm text-gray-400 mb-5">
              Save this token — you will not see it again.
            </p>
            <TokenRevealCard rawToken={rawToken} onConfirm={handleConfirm} />
          </>
        )}
      </div>
    </div>
  )
}
